import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  ConfigProvider,
  DatePicker,
  Empty,
  Pagination,
  Popconfirm,
  Select,
  Spin,
  Tooltip,
  message,
} from 'antd';
import datePickerZhCN from 'antd/es/date-picker/locale/zh_CN';
import zhCN from 'antd/es/locale/zh_CN';
import {
  deleteScenarioTaskRecord,
  getScenarioTaskOntologyList,
  getScenarioTaskRecordList,
  type ScenarioTaskOntologyOption,
  type ScenarioTaskRecordItem,
  type ScenarioTaskRecordStatus,
} from '../../api';

dayjs.locale('zh-cn');

type QuickStatusKey = 'all' | 'running' | 'completed' | 'failed';

interface SearchFormState {
  ontologyId?: number;
  startDate: string;
  endDate: string;
}

interface AppliedFilterState extends SearchFormState {
  page: number;
  size: number;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const { RangePicker } = DatePicker;

const QUICK_STATUS_OPTIONS: Array<{
  key: QuickStatusKey;
  label: string;
  statuses: ScenarioTaskRecordStatus[];
}> = [
  {
    key: 'all',
    label: '全部',
    statuses: ['running', 'completed', 'failed', 'cancelled'],
  },
  {
    key: 'running',
    label: '进行中',
    statuses: ['running'],
  },
  {
    key: 'completed',
    label: '已完成',
    statuses: ['completed'],
  },
  {
    key: 'failed',
    label: '失败',
    statuses: ['failed'],
  },
];

const STATUS_STYLE_MAP: Record<
  ScenarioTaskRecordStatus,
  { label: string; className: string }
> = {
  running: {
    label: '进行中',
    className: 'border-[#91caff] bg-[#e6f4ff] text-[#1677ff]',
  },
  completed: {
    label: '已完成',
    className: 'border-[#b7eb8f] bg-[#f6ffed] text-[#52c41a]',
  },
  failed: {
    label: '失败',
    className: 'border-[#ffccc7] bg-[#fff2f0] text-[#ff4d4f]',
  },
  cancelled: {
    label: '已取消',
    className: 'border-[#d9d9d9] bg-[#fafafa] text-[#8c8c8c]',
  },
};

const defaultSearchFormState: SearchFormState = {
  ontologyId: undefined,
  startDate: '',
  endDate: '',
};

const defaultAppliedFilterState: AppliedFilterState = {
  ...defaultSearchFormState,
  page: 1,
  size: 20,
};

const VIEW_PROCESS_ICON_PATH =
  'M475.471726 0a475.428571 475.428571 0 0 1 365.421714 779.556571l167.936 165.595429a43.885714 43.885714 0 0 1-55.442286 67.584l-6.144-5.12-168.521143-166.034286A475.428571 475.428571 0 1 1 475.471726 0z m0 87.771429a387.657143 387.657143 0 1 0 0 775.314285 387.657143 387.657143 0 0 0 0-775.314285z m0 118.491428a43.885714 43.885714 0 0 1 43.373714 36.717714l0.585143 7.168V438.857143h183.954286a43.885714 43.885714 0 0 1 43.300571 36.864l0.585143 7.094857a43.885714 43.885714 0 0 1-36.790857 43.300571l-7.094857 0.585143H475.471726a43.885714 43.885714 0 0 1-43.300572-36.790857L431.586011 482.889143V250.148571a43.885714 43.885714 0 0 1 43.885715-43.885714z';
const VIEW_RESULT_ICON_PATH =
  'M833.945898 0C938.245089 0 1023.168036 79.359471 1023.168036 176.944122v465.541457a42.205682 42.205682 0 0 1-42.461473 41.821993 42.205682 42.205682 0 0 1-42.525422-41.885941V176.944122c0-51.350246-46.74599-93.172239-104.235243-93.172239H253.170141c-57.489254 0-104.235244 41.821993-104.235244 93.172239v669.279792c0 51.350246 46.74599 93.172239 104.235244 93.17224h505.12527a42.205682 42.205682 0 0 1 42.461474 41.885941 42.205682 42.205682 0 0 1-42.461474 41.885941H253.170141C148.870949 1023.168036 63.948002 943.808566 63.948002 846.223914V176.944122C63.948002 79.359471 148.870949 0 253.170141 0h580.775757z m-200.796727 425.062371a242.490825 242.490825 0 0 1 178.223082 48.216794 235.776284 235.776284 0 0 1 50.454974 323.257151l98.479923 88.0564a41.310409 41.310409 0 0 1 3.005556 59.087954 42.717266 42.717266 0 0 1-59.983226 2.87766l-101.421531-90.550371a242.107137 242.107137 0 0 1-139.214801 43.356745 241.723449 241.723449 0 0 1-147.655938-50.071286 235.90418 235.90418 0 0 1-42.525421-334.064364A240.700281 240.700281 0 0 1 633.085223 425.062371z m30.055561 82.045287a156.161022 156.161022 0 0 0-123.675437 59.663486 152.963621 152.963621 0 0 0 27.625537 216.527936 157.248138 157.248138 0 0 0 115.55404 31.206625c85.690323-10.615368 146.568821-87.992451 135.697661-172.339866-9.91194-77.888667-77.504979-135.058181-155.201801-135.058181zM390.978086 409.011423a42.205682 42.205682 0 0 1 42.525421 41.821993 42.205682 42.205682 0 0 1-42.525421 41.885942h-97.840444a42.205682 42.205682 0 0 1-42.461473-41.885942 42.205682 42.205682 0 0 1 42.461473-41.821993h97.840444z m390.722294-192.227695a42.205682 42.205682 0 0 1 42.461473 41.821993 42.205682 42.205682 0 0 1-42.461473 41.885942H291.091306a42.205682 42.205682 0 0 1-42.461473-41.885942 42.205682 42.205682 0 0 1 42.461473-41.821993h490.609074z';

const ScenarioTaskListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [activeQuickStatus, setActiveQuickStatus] =
    useState<QuickStatusKey>('all');
  const [searchForm, setSearchForm] = useState<SearchFormState>(
    defaultSearchFormState
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilterState>(
    defaultAppliedFilterState
  );

  const statusFilter = useMemo(
    () =>
      QUICK_STATUS_OPTIONS.find(option => option.key === activeQuickStatus)
        ?.statuses || QUICK_STATUS_OPTIONS[0].statuses,
    [activeQuickStatus]
  );

  const queryParams = useMemo(
    () => ({
      status: statusFilter,
      ontologyId: appliedFilters.ontologyId
        ? [appliedFilters.ontologyId]
        : undefined,
      startDate: appliedFilters.startDate || undefined,
      endDate: appliedFilters.endDate || undefined,
      page: appliedFilters.page,
      size: appliedFilters.size,
    }),
    [statusFilter, appliedFilters]
  );

  const {
    data: recordPage,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['scenario-task-records', queryParams],
    queryFn: () => getScenarioTaskRecordList(queryParams),
  });

  const { data: ontologyOptions = [], error: ontologyError } = useQuery({
    queryKey: ['scenario-task-ontology-options'],
    queryFn: getScenarioTaskOntologyList,
  });

  useEffect(() => {
    if (error instanceof Error) {
      messageApi.error(error.message);
    }
  }, [error, messageApi]);

  useEffect(() => {
    if (ontologyError instanceof Error) {
      messageApi.error(ontologyError.message);
    }
  }, [ontologyError, messageApi]);

  const deleteMutation = useMutation({
    mutationFn: deleteScenarioTaskRecord,
    onSuccess: () => {
      messageApi.success('任务删除成功');
      if ((recordPage?.items.length || 0) <= 1 && appliedFilters.page > 1) {
        setAppliedFilters(prev => ({
          ...prev,
          page: prev.page - 1,
        }));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['scenario-task-records'] });
    },
    onError: err => {
      messageApi.error((err as Error).message || '任务删除失败');
    },
  });

  const handleApplyFilters = () => {
    const hasOnlyOneDate =
      (!!searchForm.startDate && !searchForm.endDate) ||
      (!searchForm.startDate && !!searchForm.endDate);

    if (hasOnlyOneDate) {
      messageApi.warning('请选择完整的开始日期和结束日期');
      return;
    }

    if (
      searchForm.startDate &&
      searchForm.endDate &&
      searchForm.startDate > searchForm.endDate
    ) {
      messageApi.warning('开始日期不能晚于结束日期');
      return;
    }

    setAppliedFilters(prev => ({
      ...prev,
      ontologyId: searchForm.ontologyId,
      startDate: searchForm.startDate,
      endDate: searchForm.endDate,
      page: 1,
    }));
  };

  const records: ScenarioTaskRecordItem[] = recordPage?.items || [];

  return (
    <div className="h-full min-h-0 overflow-auto px-8 py-7">
      {contextHolder}
      <div className="mx-auto w-full max-w-[1480px]">
        <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="mb-2 text-[28px] font-bold leading-none text-[#1f2937]">
              场景任务运行记录
            </h2>
          </div>

          <Button
            icon={<PlusOutlined />}
            className="!h-10 !rounded-md !border-none !bg-[#1890ff] !px-5 !text-sm !font-semibold !text-white hover:!bg-[#1677ff]"
            onClick={() => navigate('/scenario-task')}
          >
            运行新任务
          </Button>
        </div>

        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex overflow-hidden rounded-md border border-[#d9dee7] bg-white">
            {QUICK_STATUS_OPTIONS.map(option => {
              const active = option.key === activeQuickStatus;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setActiveQuickStatus(option.key);
                    setAppliedFilters(prev => ({
                      ...prev,
                      page: 1,
                    }));
                  }}
                  className={[
                    'relative min-w-[84px] cursor-pointer appearance-none border-none px-5 py-2 text-sm font-medium outline-none transition-colors after:absolute after:right-0 after:top-1/2 after:h-4 after:w-px after:-translate-y-1/2 after:bg-[#d9dee7] last:after:hidden focus:outline-none',
                    active
                      ? 'bg-[#1890ff] text-white'
                      : 'bg-white text-[#4b5563] hover:bg-[#f8fafc]',
                  ].join(' ')}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#4b5563]">选择场景:</span>
              <Select<number>
                allowClear
                showSearch
                value={searchForm.ontologyId}
                placeholder="全部"
                className="w-[190px]"
                optionFilterProp="label"
                onChange={value =>
                  setSearchForm(prev => ({
                    ...prev,
                    ontologyId: value,
                  }))
                }
                options={ontologyOptions.map(item => ({
                  value: item.ontologyId,
                  label: getOntologyLabel(item),
                }))}
              />
            </div>

            <ConfigProvider locale={zhCN}>
              <RangePicker
                value={parseDateRange(searchForm.startDate, searchForm.endDate)}
                format="YYYY-MM-DD"
                locale={datePickerZhCN}
                placeholder={['选择日期', '选择日期']}
                className="!h-9 !w-[288px] [&_.ant-picker-input>input]:text-center [&_.ant-picker-range-separator]:!px-0"
                separator={
                  <span className="inline-flex w-5 items-center justify-center text-xs text-[#bfbfbf]">
                    -
                  </span>
                }
                onChange={value => {
                  const [startDate, endDate] = formatDateRange(value);
                  setSearchForm(prev => ({
                    ...prev,
                    startDate,
                    endDate,
                  }));
                }}
              />
            </ConfigProvider>
            <Button
              icon={<SearchOutlined />}
              onClick={handleApplyFilters}
              className="!flex !h-9 !w-9 !items-center !justify-center !rounded-md !border-[#1890ff] !bg-white !p-0 !text-[#1890ff] hover:!border-[#1677ff] hover:!bg-[#f0f8ff] hover:!text-[#1677ff]"
              aria-label="搜索任务"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-[#e8edf3] bg-white">
          {isLoading ? (
            <div className="flex h-60 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <Empty description="暂无匹配的任务记录" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] border-collapse text-sm text-[#374151]">
                <thead>
                  <tr className="bg-[#fafbfd] text-left text-[#1f2937]">
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      任务ID
                    </th>
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      所属场景
                    </th>
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      状态
                    </th>
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      任务开始时间
                    </th>
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      耗时（s）
                    </th>
                    <th className="px-6 py-3 text-[13px] font-semibold shadow-[inset_0_-1px_0_0_#e9edf3]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(record => (
                    <tr
                      key={record.id}
                      className="transition-colors hover:bg-[#fafcff]"
                    >
                      <td className="px-6 py-3 text-[13px] font-medium text-[#101828] shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        {renderValue(record.id)}
                      </td>
                      <td className="px-6 py-3 text-[13px] shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        {renderValue(record.ontologyName)}
                      </td>
                      <td className="px-6 py-3 text-[13px] shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="px-6 py-3 text-[13px] whitespace-nowrap shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        {formatDateTime(record.createdAt)}
                      </td>
                      <td className="px-6 py-3 text-[13px] shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        {formatDuration(record.durationMs)}
                      </td>
                      <td className="px-6 py-3 text-[13px] shadow-[inset_0_-1px_0_0_#e3e8ef]">
                        <div className="flex items-center gap-3">
                          <Tooltip
                            title={
                              record.status === 'completed'
                                ? '查看运行结果'
                                : '查看运行过程'
                            }
                          >
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/scenario-task?id=${record.id}`)
                              }
                              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-base text-[#1890ff] transition-colors hover:bg-[#f0f8ff] hover:text-[#1677ff]"
                              aria-label={`查看任务 ${record.id} 详情`}
                            >
                              {record.status === 'completed' ? (
                                <ViewResultIcon />
                              ) : (
                                <ViewProcessIcon />
                              )}
                            </button>
                          </Tooltip>
                          <Popconfirm
                            title="确认删除该任务吗？"
                            okText="删除"
                            cancelText="取消"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => deleteMutation.mutate(record.id)}
                          >
                            <Tooltip title="删除">
                              <button
                                type="button"
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-base text-[#ff4d4f] transition-colors hover:bg-[#fff1f0] hover:text-[#ff4d4f]"
                                aria-label={`删除任务 ${record.id}`}
                              >
                                <svg
                                  viewBox="0 0 1024 1024"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M781.28 851.36a58.56 58.56 0 0 1-58.56 58.56H301.28a58.72 58.72 0 0 1-58.56-58.56V230.4h538.56z m-421.6-725.92a11.84 11.84 0 0 1 12-12h281.28a11.84 11.84 0 0 1 12 12V160H359.68zM956.8 160H734.72v-34.56a81.76 81.76 0 0 0-81.76-81.76H371.68a82.08 82.08 0 0 0-81.76 81.76V160H67.2a35.36 35.36 0 0 0 0 70.56h105.12v620.8a128.96 128.96 0 0 0 128.96 128.96h421.44a128.96 128.96 0 0 0 128.96-128.96V230.4H956.8a35.2 35.2 0 0 0 35.2-35.2 34.56 34.56 0 0 0-35.2-35.2zM512 804.16a35.2 35.2 0 0 0 35.2-35.36V393.92a35.2 35.2 0 1 0-70.4 0V768.8a35.2 35.2 0 0 0 35.2 35.36m-164.32 0a35.36 35.36 0 0 0 35.36-35.36V393.92a35.36 35.36 0 1 0-70.56 0V768.8a36.32 36.32 0 0 0 35.2 35.36m328.64 0a35.36 35.36 0 0 0 35.2-35.36V393.92a35.36 35.36 0 1 0-70.56 0V768.8a35.36 35.36 0 0 0 35.36 35.36"
                                    fill="#D81E06"
                                  ></path>
                                </svg>
                              </button>
                            </Tooltip>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-5 grid items-center gap-3 md:grid-cols-[1fr_auto_1fr]">
          <div className="text-sm text-[#6b7280]">
            {isFetching
              ? '数据更新中...'
              : `共 ${recordPage?.total || 0} 条任务记录`}
          </div>

          <div className="flex justify-center">
            <Pagination
              current={appliedFilters.page}
              pageSize={appliedFilters.size}
              total={recordPage?.total || 0}
              showSizeChanger={false}
              onChange={page => {
                setAppliedFilters(prev => ({
                  ...prev,
                  page,
                }));
              }}
            />
          </div>

          <div className="flex justify-start md:justify-end">
            <div className="flex items-center gap-2 text-sm text-[#4b5563]">
              <span>每页</span>
              <Select<number>
                value={appliedFilters.size}
                className="w-[110px]"
                onChange={size => {
                  setAppliedFilters(prev => ({
                    ...prev,
                    page: 1,
                    size,
                  }));
                }}
                options={PAGE_SIZE_OPTIONS.map(size => ({
                  value: size,
                  label: `${size} 条/页`,
                }))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewProcessIcon: React.FC = () => {
  return (
    <svg
      viewBox="0 0 1024 1024"
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d={VIEW_PROCESS_ICON_PATH} fill="currentColor"></path>
    </svg>
  );
};

const ViewResultIcon: React.FC = () => {
  return (
    <svg
      viewBox="0 0 1024 1024"
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d={VIEW_RESULT_ICON_PATH} fill="currentColor"></path>
    </svg>
  );
};

const StatusBadge: React.FC<{ status: ScenarioTaskRecordStatus }> = ({
  status,
}) => {
  const config = STATUS_STYLE_MAP[status] || STATUS_STYLE_MAP.cancelled;

  return (
    <span
      className={`inline-flex min-w-[64px] items-center justify-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
};

function getOntologyLabel(item: ScenarioTaskOntologyOption): string {
  return item.ontologyName?.trim() || '-';
}

function parseDateRange(
  startDate: string,
  endDate: string
): [Dayjs, Dayjs] | null {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate).locale('zh-cn');
  const end = dayjs(endDate).locale('zh-cn');

  if (!start.isValid() || !end.isValid()) return null;

  return [start, end];
}

function formatDateRange(
  value: [Dayjs | null, Dayjs | null] | null | undefined
): [string, string] {
  if (!value) return ['', ''];

  const [start, end] = value;

  return [
    start ? start.format('YYYY-MM-DD') : '',
    end ? end.format('YYYY-MM-DD') : '',
  ];
}

function renderValue(value: React.ReactNode): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return value;
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.replace('T', ' ').slice(0, 16) || '-';
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())} ${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;
}

function formatDuration(durationMs: number | null): string {
  if (durationMs === null || durationMs === undefined) return '-';

  const seconds = durationMs / 1000;
  if (Number.isInteger(seconds)) {
    return String(seconds);
  }

  return String(Number(seconds.toFixed(1)));
}

function padNumber(value: number): string {
  return String(value).padStart(2, '0');
}

export default ScenarioTaskListPage;
